import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Badge } from '@avagency/frontend/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { cn } from '@avagency/frontend/lib/utils';
import { fetchDashboard, type DashboardMetrics } from '../api';

type MetricVariant = 'good' | 'warn' | 'danger' | undefined;

export function Dashboard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['popilot', 'dashboard'],
    queryFn: fetchDashboard,
    refetchInterval: 60_000,
  });

  if (isLoading) return <LoadingState rows={4} />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;
  if (!data) return null;

  const m = data.metrics;

  return (
    <div className="space-y-6">
      <BillingBar metrics={m} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <MetricCard
          label="Open POs"
          value={m.draft_pos + m.sent_pos + m.confirmed_pos}
          sub={`${m.draft_pos} draft · ${m.sent_pos} sent · ${m.confirmed_pos} confirmed`}
        />
        <MetricCard
          label="OOS SKUs"
          value={m.oos_skus}
          sub={`of ${m.total_skus} total SKUs`}
          variant={m.oos_skus > 0 ? 'danger' : 'good'}
        />
        <MetricCard
          label="Unconfigured SKUs"
          value={m.unconfigured_skus}
          sub="missing reorder config"
          variant={m.unconfigured_skus > 0 ? 'warn' : undefined}
        />
        <MetricCard
          label="Received this month"
          value={m.received_pos}
          sub="completed POs"
          variant="good"
        />
        <MetricCard
          label="Cancelled"
          value={m.cancelled_pos}
          sub="POs cancelled (all time)"
        />
      </div>

      <Card className="po-card-glow">
        <CardHeader>
          <CardTitle>PO status breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <POStatusBreakdown metrics={m} />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Billing bar ────────────────────────────────────────────────────

function BillingBar({ metrics }: { metrics: DashboardMetrics }) {
  const unlimited = metrics.monthly_po_cap < 0;
  const pct = unlimited
    ? 0
    : Math.round((metrics.pos_this_month / metrics.monthly_po_cap) * 100);
  const fillClass =
    pct >= 90 ? 'bg-destructive' : pct >= 70 ? 'bg-yellow-500' : 'po-billing-fill';

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 py-4">
        <Badge variant="secondary" className="uppercase">
          {metrics.billing_tier}
          {metrics.trial_active ? ' (trial)' : ''}
        </Badge>
        {unlimited ? (
          <span className="text-sm text-muted-foreground">Unlimited POs/month</span>
        ) : (
          <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-xs text-muted-foreground sm:whitespace-nowrap">
              {metrics.pos_this_month} / {metrics.monthly_po_cap} POs this month
            </span>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted sm:flex-1">
              <div
                className={cn('h-full transition-all', fillClass)}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {metrics.total_skus} total SKUs
        </span>
      </CardContent>
    </Card>
  );
}

// ─── PO status breakdown ─────────────────────────────────────────────

type StatusRow = {
  label: string;
  count: number;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
};

function POStatusBreakdown({ metrics }: { metrics: DashboardMetrics }) {
  const rows: StatusRow[] = [
    { label: 'Draft', count: metrics.draft_pos, variant: 'outline' },
    { label: 'Sent', count: metrics.sent_pos, variant: 'secondary' },
    { label: 'Confirmed', count: metrics.confirmed_pos, variant: 'default' },
    { label: 'Received', count: metrics.received_pos, variant: 'default' },
    { label: 'Cancelled', count: metrics.cancelled_pos, variant: 'destructive' },
  ];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Count</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.label}>
            <TableCell>
              <Badge variant={r.variant}>{r.label}</Badge>
            </TableCell>
            <TableCell className="text-right tabular-nums">{r.count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─── Reusable metric card ─────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
  variant,
}: {
  label: string;
  value: number;
  sub?: string;
  variant?: MetricVariant;
}) {
  const valueClass =
    variant === 'danger'
      ? 'text-destructive'
      : variant === 'warn'
        ? 'text-yellow-600 dark:text-yellow-400'
        : variant === 'good'
          ? 'metric-value-good'
          : 'text-foreground';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">
          {label}
        </CardDescription>
        <CardTitle className={cn('text-3xl tabular-nums', valueClass)}>
          {value.toLocaleString()}
        </CardTitle>
      </CardHeader>
      {sub && (
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">{sub}</p>
        </CardContent>
      )}
    </Card>
  );
}
